import React, { useRef, useState } from 'react';
import { Upload, Loader2, ExternalLink, Video as VideoIcon, Image as ImageIcon, X } from 'lucide-react';
import { api } from '../lib/api';

const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif,image/avif';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,video/ogg';

/**
 * Reusable media field: paste a URL OR upload a file.
 * `kind` = 'image' (default) | 'video'
 * value is always the URL string.
 */
const ImageInput = ({ label, value, onChange, testid, hint, kind = 'image' }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const isVideo = kind === 'video';
    const accept = isVideo ? VIDEO_ACCEPT : IMAGE_ACCEPT;
    const Icon = isVideo ? VideoIcon : ImageIcon;

    const openPicker = () => fileRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError('');
        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            // Let axios/browser set the multipart boundary automatically
            const { data } = await api.post('/admin/upload', form);
            onChange(data.url);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Upload failed');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div>
            <label className="label-caps text-white/60 mb-2 flex items-center gap-2">
                <Icon className="h-3.5 w-3.5" /> {label}
            </label>
            <div className="flex gap-2">
                <input
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={isVideo ? 'Paste a video URL or upload →' : 'Paste an image URL or upload →'}
                    className="flex-1 bg-brand-bg border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                    data-testid={testid}
                />
                <button
                    type="button"
                    onClick={openPicker}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-ink transition-colors px-4 py-3 label-caps disabled:opacity-50"
                    data-testid={`${testid}-upload`}
                    title={value ? 'Replace with a new file' : (isVideo ? 'Upload a video file' : 'Upload an image file')}
                >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Uploading' : (value ? 'Replace' : 'Upload')}
                </button>
                {value && !uploading && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        className="inline-flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors px-4 py-3 label-caps"
                        data-testid={`${testid}-clear`}
                        title="Remove this file"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={handleFile}
                />
            </div>
            {hint && <div className="text-xs text-white/40 mt-1.5">{hint}</div>}
            {error && <div className="text-xs text-red-300 mt-1.5" data-testid={`${testid}-error`}>{error}</div>}
            {value && (
                <div className="mt-2 flex items-center gap-3">
                    {isVideo ? (
                        <video src={value} muted autoPlay loop playsInline className="h-16 w-24 object-cover bg-black border border-white/10" />
                    ) : (
                        <img src={value} alt="preview" className="h-14 w-14 object-contain bg-white/5 border border-white/10" />
                    )}
                    <a href={value} target="_blank" rel="noreferrer" className="text-xs text-white/40 hover:text-white transition-colors inline-flex items-center gap-1">
                        Open <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            )}
        </div>
    );
};

export default ImageInput;
